import { withExtraConfig } from '@studiometa/js-toolkit';
import { ModalWithTransition } from '@studiometa/ui';

/**
 * NoiseReduction component
 *
 * Plays two audio tracks (with vs. without noise reduction) in sync with the
 * modal video. Both tracks run simultaneously, the toggle just flips which one
 * is audible via volume — switching is instantaneous and never loses time.
 */
export default class NoiseReduction extends withExtraConfig(ModalWithTransition, {
  name: 'NoiseReduction',
  refs: [
    'toggle',
    'video',
    'audioWith',
    'audioWithout',
    'rewind',
    'modal',
    'close[]',
    'overlay',
    'container',
  ],
}) {
  /**
   *
   */
  mounted() {
    super.mounted();
    this.$refs.video.muted = true;
    this.applyVolumes();
  }

  /**
   *
   */
  async open() {
    await super.open();
    this.$refs.video.play();
  }

  /**
   *
   */
  close() {
    super.close();
    this.$refs.video.pause();
  }

  /**
   * Set the active track's volume to 1 and mute the other.
   */
  applyVolumes() {
    const useWith = this.$refs.toggle.checked;
    this.$refs.audioWith.muted = !useWith;
    this.$refs.audioWithout.muted = useWith;
  }

  /**
   * Snap both audios to the video's currentTime when they're far enough behind.
   * @param {number} time
   */
  syncAudios(time) {
    for (const audio of this.audios) {
      if (audio.readyState < 1) continue;
      if (Math.abs(audio.currentTime - time) > 0.2) audio.currentTime = time;
    }
  }

  /**
   *
   */
  onVideoPlay() {
    this.syncAudios(this.$refs.video.currentTime);
    for (const audio of this.audios) audio.play();
  }

  /**
   *
   */
  onVideoPause() {
    for (const audio of this.audios) audio.pause();
  }

  /**
   *
   */
  onVideoSeeked() {
    this.syncAudios(this.$refs.video.currentTime);
  }

  /**
   *
   */
  onVideoTimeupdate() {
    this.syncAudios(this.$refs.video.currentTime);
  }

  /**
   *
   */
  onToggleChange() {
    this.applyVolumes();
  }

  /**
   * Reveal the rewind overlay when the video reaches its end.
   */
  onVideoEnded() {
    this.$refs.rewind.classList.remove('opacity-0', 'pointer-events-none');
  }

  /**
   * Restart the video (and the two audio tracks) from zero, then hide the
   * rewind overlay again.
   */
  onRewindClick() {
    this.$refs.rewind.classList.add('opacity-0', 'pointer-events-none');
    this.$refs.video.currentTime = 0;
    for (const audio of this.audios) audio.currentTime = 0;
    this.$refs.video.play();
  }

  /**
   * Iterable of the two audio elements for shorthand operations.
   * @returns {HTMLAudioElement[]}
   */
  get audios() {
    return [this.$refs.audioWith, this.$refs.audioWithout];
  }
}
